import { useField } from "formik";
import { ChangeEvent, ReactNode } from "react";
import { VariantProps, cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

export const inputVariants = cva(
  "w-full p-4 text-[14px] border border-[#ccc] rounded-[8px] outline-none ring-0 text-primary-900",
  {
    variants: {
      variant: {
        default:
          "bg-white placeholder-primary-text-200 data-[placeholder]:text-primary-text-500",
        primary:
          "bg-primary-50 placeholder-primary-text-200 data-[placeholder]:text-primary-text-200  shadow-[0px_1.72px_3.45px_0px_rgba(16,_24,_40,_0.05)] border-none",
        conversion:
          "bg-white placeholder-primary-text-200 data-[placeholder]:text-primary-text-200 shadow-[0px_1.72px_3.45px_0px_rgba(16,_24,_40,_0.08)] border-none",
      },
      radius: {
        sm: "rounded-[8px]",
        full: "rounded-full px-[24px]",
      },
      height: {
        default: "h-[44px]",
        lg: "h-12",
        xl: "h-[73px] text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      radius: "full",
    },
  },
);

interface TextInputProps extends VariantProps<typeof inputVariants> {
  name: string;
  label: string;
  children?: ReactNode;
  placeholder?: string;
  type?: string;
  className?: string;
  onChange?: (value: string) => void;
}

export const TextInput = ({
  name,
  label,
  children,
  variant,
  height,
  radius,
  onChange,
  className = "",
  type = "text",
  ...props
}: TextInputProps) => {
  const [field, meta] = useField(name);
  const hasError = meta.touched && meta.error;

  const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    field.onChange(e);
    onChange?.(e.target.value);
  };
  return (
    <div className={cn("w-full relative", className)}>
      <label
        htmlFor={name}
        className="block text-xs sm:text-sm lg:text-lg mb-1 text-primary-text-600"
      >
        {label}
        {children}
      </label>
      <input
        type={type}
        id={name}
        {...field}
        {...props}
        onChange={handleValueChange}
        className={cn(
          inputVariants({ height, variant, radius }),
          `${
            hasError
              ? "!border-red-500 text-red-500 placeholder:!text-red-500"
              : ""
          }`,
        )}
      />
      <p
        className={`absolute -bottom-3 left-0 h-2 text-xs text-red-500 pl-3 mt-1 ${hasError ? "visible" : "invisible"}`}
      >
        {meta.error}
      </p>
    </div>
  );
};
